// 📊 인포그래픽 오버레이 스펙(P2 정식 축, 회장님 2026-07-11) — 씬 위에 얹는 "움직이는 정보 그래픽".
//   ★두 렌더러 공유 SSOT★: 바로제작 합성기(mediabunnyRender 캔버스)와 에이전트(StoryTemplate React)가
//   같은 아이템 스펙·타이밍 함수로 그린다 — 좌표는 캔버스 정규화(0..1 중심), 시각은 컷/씬 상대 초.
//   종류 4종(P2): badge(강조 배지 pop-in) · price(가격 카드) · stat(숫자 카운트업) · arrow(화살표+라벨).
//   P2b(후속): sticker(사전 렌더 Lottie webm/이미지) — 합성기 프레임 루프의 비디오 소스 관리가 필요해 분리.

//   P3(2026-08-03 · 스타일 그래픽 오버레이) 4종 추가: panelCard(문제판) · glyphReveal(대형 글리프) ·
//   bannerBurst(하단 버스트 배너) · titleBand(상단 타이틀 밴드). ★새 렌더 축이 아니다★ — 기존 8종과
//   같은 아이템 스펙·타이밍 함수·좌표계를 쓰고, 같은 UI(InfographicsPanel·SceneInfographicsSection)로
//   수정·삭제된다(막다른 길 금지 헌법). 생성 모델이 화면에 글자를 그리다 뭉개지는 문제를
//   "클립=무대 / 텍스트=레이어" 분업으로 원천 차단하는 것이 이 4종의 존재 이유다.
export {
  infographicCaptionSafeTop,
  type InfographicAspect,
} from "../../supabase/functions/_shared/infographic-safe-area";
import { infographicCaptionSafeTop as captionSafeTopOf, type InfographicAspect } from "../../supabase/functions/_shared/infographic-safe-area";
// ★상대 경로★ — 이 파일은 정밀 편집 앱 번들(esbuild · `@/` 별칭 없음)에도 들어간다.
import { SAFE_ZONE, centeredSafeWidth, nearestSafeAspect } from "./safe-zone";
import { visualEmWidth } from "./text/wrap-display-line";

export type InfographicKind =
  | "badge" | "price" | "stat" | "arrow" | "list" | "compare" | "ring" | "bar"
  | "panelCard" | "glyphReveal" | "bannerBurst" | "titleBand";

/**
 * 🎨 스킨 — 스타일(구도)이 고르는 **룩 프리셋**. 색 하나(color)로는 네온/골드/종이 같은 결이 안 나온다
 *   (테두리 두께·글로우·배경 불투명도가 함께 움직여야 한다). 스타일이 선언하고, 유저는 기존 color 로
 *   포인트 색만 덮어쓸 수 있다(resolveInfographicSkin 이 병합).
 */
export type InfographicSkin = "neon" | "gold" | "paper" | "plain";

export interface InfographicSkinSpec {
  /** 포인트 색 — 테두리·글리프·강조 텍스트. item.color 가 있으면 그 값이 이긴다. */
  accent: string;
  /** 패널/밴드 배경(canvas fillStyle·CSS background 양쪽에서 그대로 쓰이는 문자열). */
  bg: string;
  /** 본문 글자색. */
  fg: string;
  /** 글로우 반경 배율(기준 글자 높이 대비 · 0=글로우 없음). */
  glow: number;
  /** 테두리 두께 배율(기준 글자 높이 대비 · 0=테두리 없음). */
  border: number;
  /** 글자 굵기(CSS font-weight). */
  weight: number;
  /** 등장 순간 화이트 플래시 세기(0=없음) — glyphReveal 의 "한 번에 팝으로 터진다" 연출. */
  flash: number;
}

export const INFOGRAPHIC_SKINS: Record<InfographicSkin, InfographicSkinSpec> = {
  neon: { accent: "#37E6FF", bg: "rgba(8,12,28,0.78)", fg: "#ffffff", glow: 0.55, border: 0.09, weight: 900, flash: 0.45 },
  gold: { accent: "#F6C445", bg: "rgba(26,17,4,0.82)", fg: "#fff8e1", glow: 0.3, border: 0.07, weight: 900, flash: 0.25 },
  paper: { accent: "#1f2937", bg: "rgba(250,248,242,0.96)", fg: "#1f2937", glow: 0, border: 0.05, weight: 800, flash: 0 },
  plain: { accent: "#FF5A5F", bg: "rgba(17,17,17,0.82)", fg: "#ffffff", glow: 0, border: 0.04, weight: 800, flash: 0 },
};

/** 스킨 미지정 아이템의 기본값 — 기존 8종의 룩(다크 카드 + 포인트색)과 같은 결. */
export const INFOGRAPHIC_DEFAULT_SKIN: InfographicSkin = "plain";

export interface InfographicItem {
  kind: InfographicKind;
  /** 주 텍스트 — badge/arrow=문구, price=상품·맥락 라벨, stat=단위 포함 라벨("만원"·"%"). */
  text: string;
  /** price=가격 문자열("19,900원"), stat=목표 숫자(카운트업 도달값). */
  value?: string | number;
  /** 보조 텍스트 — price=취소선 원가("39,800원"), stat=접미사("%"·"만원"), badge/arrow 미사용. */
  sub?: string;
  x: number; // 정규화 중심 X(0..1)
  y: number; // 정규화 중심 Y(0..1)
  /** 기준 글자 높이 = sizePct × 캔버스 높이(기본 kind 별 프리셋). */
  sizePct?: number;
  /** 포인트 컬러(배지 배경·화살표·강조 숫자). 기본 #FF5A5F 계열. */
  color?: string;
  /** 표시 창(컷/씬 상대 초). 없으면 컷 전체. 등장 애니는 start 기준 0.45s. */
  start?: number;
  end?: number;
  /** arrow 전용 — 화살표가 가리키는 지점(정규화). 없으면 라벨 아래 45°. */
  tip?: { x: number; y: number };
  /** list 전용 — 순차 등장 불릿 줄(최대 6·각 24자). compare 는 text=왼쪽·sub=오른쪽 라벨.
   *  panelCard 는 본문 줄(없으면 sub 한 줄을 본문으로). */
  items?: string[];
  /** 🎨 룩 프리셋(P3 4종 전용 · 미지정=plain). 기존 8종은 이 값을 읽지 않는다(룩 불변). */
  skin?: InfographicSkin;
  /**
   * 🏷 출처 마커 — "style" = **(구)스타일 선언 파생 아이템**(🪦 파생 축은 2026-08-08 철거 — 새로
   *   만들어지지 않고 기존 저장 작업물에만 남는다). 유저가 직접 얹은 아이템에는
   *   붙지 않는다. 재시드·스타일 교체 때 이 마커가 붙은 것만 갈아끼우고 유저 아이템은 보존한다
   *   (replaceStyleInfographics). 저장분에 남은 마커 아이템은 하이드레이트·렌더 경계에서 소거된다
   *   (dropRetiredStyleInfographics — 미충전 플레이스홀더라 화면에 내보내지 않는다).
   *   ★유저가 편집 UI 로 손대면 폼 값으로 새 아이템이 만들어지므로 마커가
   *   자연히 떨어져 유저 소유로 승격된다 — normalizeInfographic 은 "style" 이외의 값을 받지 않는다.
   */
  source?: "style";
}

/** 등장 진행도(0..1) — start 기준 dur 초 ease-out-back(살짝 오버슈트 후 안착). 양 렌더러 공용. */
export function enterProgress(tRel: number, start = 0, dur = 0.45): number {
  const t = (tRel - start) / dur;
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const c1 = 1.70158;
  const c3 = c1 + 1;
  const u = t - 1;
  return 1 + c3 * u * u * u + c1 * u * u;
}

/** 카운트업 현재값 — start+0.15s 부터 1.1s 동안 ease-out 으로 목표 도달(끝값 고정·정수 보간). */
export function statCountAt(tRel: number, target: number, start = 0): number {
  const t = (tRel - start - 0.15) / 1.1;
  if (t <= 0) return 0;
  if (t >= 1) return target;
  const eased = 1 - Math.pow(1 - t, 3);
  return Math.round(target * eased);
}

/** 아이템이 이 시각에 보이는가 — start/end 창(없으면 전체). */
export function infographicVisibleAt(item: Pick<InfographicItem, "start" | "end">, tRel: number): boolean {
  if (item.start != null && tRel < item.start) return false;
  if (item.end != null && tRel > item.end) return false;
  return true;
}

/** kind 별 기본 글자 높이(sizePct). */
export const INFOGRAPHIC_DEFAULT_SIZE: Record<InfographicKind, number> = {
  badge: 0.042,
  price: 0.05,
  stat: 0.075,
  arrow: 0.036,
  list: 0.032,
  compare: 0.04,
  ring: 0.09, // ring 은 글자가 아니라 반경 기준(캔버스 높이 비율)
  bar: 0.034,
  panelCard: 0.036,   // 본문 글자 높이(제목은 ×1.25)
  glyphReveal: 0.3,   // ★글리프 자체의 높이 — 화면 높이의 30%(대형 리빌)
  bannerBurst: 0.05,
  titleBand: 0.046,
};

export const INFOGRAPHIC_DEFAULT_COLOR = "#FF5A5F";

/** 최종 canvas가 확립한 인포그래픽 글자 px 규약 — 양 렌더러 모두 최소 10px·정수 반올림. */
export const INFOGRAPHIC_MIN_FONT_PX = 10;

export function infographicFontPx(sizePct: number, canvasHeight: number): number {
  return Math.max(INFOGRAPHIC_MIN_FONT_PX, Math.round(sizePct * canvasHeight));
}

/**
 * 📐 **양 렌더러 공용 레이아웃 규칙**(단위 em = 그 아이템의 글자 높이 fs 배수 · pct = 캔버스 폭 비율).
 *   ★같은 값을 캔버스(drawInfographics)와 React(SceneInfographics)가 각자 적어 두면 같은 아이템이
 *     두 화면에서 다른 폭으로 뜬다★(Codex 1R P2-6 실측: panelCard 최소폭 fs*11 vs fs*9).
 *   그래서 폭·여백을 결정하는 숫자는 전부 여기 한 곳에만 있고, 양쪽은 읽어 쓰기만 한다.
 */
export const INFOGRAPHIC_LAYOUT = {
  panelCard: {
    /** 텍스트 영역 최소 폭(제목·본문이 더 넓으면 그쪽이 이긴다) */ minTextEm: 9,
    /** 좌우 안쪽 여백(한쪽) */ padXEm: 1.1,
    /** 위아래 안쪽 여백(한쪽) */ padYEm: 0.55,
    /** 제목 글자 크기 배수 */ titleEm: 1.25,
    /** 본문 줄 높이 */ lineEm: 1.5,
    /** 모서리 반경 */ radiusEm: 0.7,
  },
  /**
   * 💥 하단 버스트 배너 = 풀와이드(캔버스 폭 비율). x 는 무시하고 항상 가로 중앙.
   *   ★높이·톱니·글로우 배수는 `infographicBannerBurstMetrics` 로만 읽는다★.
   */
  bannerBurst: {
    widthPct: 0.98,
    /** 몸통 높이 — 1줄(text 만) */ oneLineHeightEm: 2.1,
    /** 몸통 높이 — 2줄(text + sub) */ twoLineHeightEm: 3.1,
    /** 위아래 톱니가 몸통 밖으로 뻗는 길이(한쪽) */ toothEm: 0.28,
    /** 스킨 glow → 실제 번짐 반경 배수(canvas shadowBlur · CSS drop-shadow 공용) */ glowBlurEm: 1.6,
  },
  /**
   * 🎫 상단 타이틀 밴드 — 최종 canvas의 확립된 높이 룩을 React도 그대로 소비한다.
   *   ★높이·글로우·테두리 배수는 `infographicTitleBandMetrics`(px)·`infographicTitleBandMetricsEm`(em)
   *   으로만 읽는다★ — 여기 숫자를 소비처가 직접 곱하기 시작하면 산식 다원화가 그대로 재발한다.
   */
  titleBand: {
    maxWidthPct: 0.94,
    minTextEm: 8,
    baseHeightEm: 1.5,
    lineHeightEm: 1.25,
    badgeFontEm: 0.62,
    badgeStackEm: 1.5,
    /** 스킨 glow → 실제 번짐 반경 배수(canvas shadowBlur · CSS box-shadow 공용) */ glowBlurEm: 1.8,
    /** 스킨 border → 테두리 선 두께 배수 */ borderWidthEm: 1.6,
    /** 테두리 최소 두께(px) — 아주 작은 글자에서도 선이 사라지지 않게 한다. */ borderMinPx: 1.5,
  },
} as const;

/** 스킨에서 밴드 기하가 실제로 읽는 것 — `InfographicSkinSpec` 을 그대로 넘겨도 된다. */
export interface InfographicBandSkinInput {
  glow?: number;
  border?: number;
}

/**
 * 밴드 줄 수 정규화 — 렌더러는 0~2줄만 그린다.
 * ★비유한 입력은 **더 큰 쪽(2줄)** 으로 떨어뜨린다★ — 이 값이 경고 판정까지 흘러가므로 NaN 이 새면
 *   `NaN > safeTop` 이 false 라 경고가 조용히 꺼진다(fail-open).
 */
function titleBandLineCount(lineCount: number): number {
  if (!Number.isFinite(lineCount)) return 2;
  return Math.max(0, Math.min(2, Math.floor(lineCount)));
}

/**
 * 밴드 세로 높이 — px(unit=fontPx · badgeUnit=badgeFontPx)와 em(unit=1 · badgeUnit=badgeFontEm)을
 * **한 식**으로 낸다. 두 단위계가 각자 식을 적으면 그게 바로 이 이슈가 없앤 산식 다원화다.
 */
function titleBandHeight(unit: number, lines: number, hasBadge: boolean, badgeUnit: number): number {
  const layout = INFOGRAPHIC_LAYOUT.titleBand;
  return unit * (layout.baseHeightEm + lines * layout.lineHeightEm)
    + (hasBadge ? badgeUnit * layout.badgeStackEm : 0);
}

/** 음수·비유한 스킨 값은 하단을 위로 당기지 못한다(0 으로 떨어뜨린다). */
function bandSkinAmount(value: number | undefined): number {
  return Number.isFinite(value) ? Math.max(0, value as number) : 0;
}

/**
 * 🎫 **타이틀 밴드 기하 SSOT(px)** — 캔버스 렌더러(`drawInfographics`)와 React 렌더러
 *   (`SceneInfographics`)가 읽는다. 배치판 경고는 정규화 좌표계라 **같은 상수·같은 높이 식**을 em 으로
 *   내는 `infographicTitleBandMetricsEm` 을 읽는다.
 *
 *   ★왜 px·em 두 함수인가★ — 뱃지 글자는 `Math.round(fontPx × badgeFontEm)` 으로 **정수 px 로 반올림**
 *     된다. 그래서 이 함수에 `fontPx = 1` 을 넣는 식으로는 em 을 못 뽑는다(round(0.62)=1 → 뱃지 스택이
 *     0.93em 이 아니라 1.5em 이 된다). 갈라지는 것은 그 반올림 하나뿐이고, 높이 식과 레이아웃 상수는
 *     `titleBandHeight`·`INFOGRAPHIC_LAYOUT.titleBand` 로 공유한다.
 */
export function infographicTitleBandMetrics(
  fontPx: number,
  lineCount: number,
  hasBadge: boolean,
  skin?: InfographicBandSkinInput,
): {
  badgeFontPx: number;
  heightPx: number;
  badgeCenterY: number | null;
  badgeTextOffsetY: number;
  titleCenterYs: number[];
  /** 스킨 글로우의 실제 번짐 반경(0 이면 글로우 없음) */ glowBlurPx: number;
  /** 테두리 선 두께(최소 두께 하한 반영 · 0 이면 테두리 없음) */ borderWidthPx: number;
  /** 중심 y → **보이는 하단**(몸통 반높이 + 글로우·테두리 중 더 멀리 뻗는 쪽) */ bottomOffsetPx: number;
} {
  const layout = INFOGRAPHIC_LAYOUT.titleBand;
  const lines = titleBandLineCount(lineCount);
  const badgeFontPx = Math.round(fontPx * layout.badgeFontEm);
  const heightPx = titleBandHeight(fontPx, lines, hasBadge, badgeFontPx);
  const glow = bandSkinAmount(skin?.glow);
  const border = bandSkinAmount(skin?.border);
  // ★곱셈 결합 순서를 종전 렌더러와 똑같이 둔다★ — `fontPx * glow * 1.8` 을 `fontPx * (glow * 1.8)` 로
  //   바꾸면 마지막 비트가 달라진다. React 쪽은 이 값이 CSS 문자열로 나가므로, 룩이 안 바뀌는
  //   리팩터링인데 DOM 스냅샷에 diff 가 생긴다.
  const glowBlurPx = glow > 0 ? fontPx * glow * layout.glowBlurEm : 0;
  const borderWidthPx = border > 0
    ? Math.max(layout.borderMinPx, fontPx * border * layout.borderWidthEm)
    : 0;
  let cursorY = -heightPx / 2 + fontPx * 0.75;
  const badgeCenterY = hasBadge ? cursorY : null;
  if (hasBadge) cursorY += badgeFontPx * layout.badgeStackEm;
  cursorY += fontPx * 0.5;
  return {
    badgeFontPx,
    heightPx,
    badgeCenterY,
    badgeTextOffsetY: badgeFontPx * 0.04,
    titleCenterYs: Array.from({ length: lines }, (_, index) => cursorY + index * fontPx * layout.lineHeightEm),
    glowBlurPx,
    borderWidthPx,
    bottomOffsetPx: heightPx / 2 + Math.max(glowBlurPx, borderWidthPx / 2),
  };
}

/**
 * 🎫 **타이틀 밴드 기하 SSOT(em)** — 배치판 경고(`estimateInfographicVisualBottom`)가 읽는다.
 *   위 px 판과 **같은 레이아웃 상수·같은 높이 식**을 쓴다(제4의 산식 금지 — 밴드 높이·글로우·테두리가
 *   필요하면 둘 중 하나를 부른다).
 *
 *   ★박제 — 종전 배치판 경고는 어떻게 갈라져 있었나(#1462 로 신설된 그 순간부터 · 트렁크 실측)★
 *     종전 식 = `y + size × ((lines×1.25 + (뱃지 ? 1.3168 : 0) + 1.2) / 2 + 1)`. 세 상수 전부 실렌더와
 *     다르다 — 실렌더는 처음부터 `infographicTitleBandMetrics` 를 읽고 있었는데 경고만 손으로 다시 적었다.
 *       · `1.2`    ← 실제 기본 높이는 `baseHeightEm` **1.5** (0.3em 과소)
 *       · `1.3168` ← 실제 뱃지 스택은 `badgeFontEm × badgeStackEm` = 0.62 × 1.5 = **0.93** (0.39em 과대)
 *       · `+1`     ← **네온 글로우의 어림값**(0.55 × 1.8 = 0.99)인데 **스킨과 무관하게 상시** 더했다
 *     ⇒ 글로우가 0 인 기본 스킨(plain·paper)에도 1em(기본 크기 0.046 · 1920p 에서 88px)을 허구로 얹어,
 *       실렌더가 안 겹치는 밴드에 '자막 안전선 아래' 경고가 상시 붙었다.
 *   ⇒ 정본은 **실제로 렌더되는 결과**다. 경고도 여기서 나온 `bottomOffsetEm` 하나만 쓴다.
 *
 * ⚠️ **하단을 정하는 것은 글로우와 테두리 중 더 멀리 뻗는 쪽이다(합이 아니다)** — 둘 다 몸통 하단
 *   `heightEm/2` 에서 바깥으로 뻗고 겹쳐 그려지므로, 더 먼 쪽이 보이는 하단이다. 더하면 실렌더보다
 *   아래로 판정해 없앤 오탐이 다시 생긴다.
 * ⚠️ **테두리는 캔버스 기준이다** — 캔버스 stroke 는 경로에 중심을 둬 선 두께의 절반이 몸통 밖으로
 *   나가지만, React 쪽은 `boxSizing: "border-box"` 라 테두리가 **안쪽**에 그려진다. 최종으로 굽는 렌더가
 *   캔버스이므로 더 바깥인 캔버스를 기준으로 잰다(React 는 이보다 안쪽이라 경고가 놓치지 않는다).
 * ⚠️ **em ↔ 정규화 등가는 ±2.1px 근사다(0 이 아니다)** — `fs = infographicFontPx(sizePct, H)` 도, 뱃지
 *   글자도 정수로 반올림되는데 여기서는 반올림 전 `badgeFontEm` 을 쓴다. 슬라이더 전 구간(0.01~0.2) ×
 *   스킨 4종 × 줄수 5형 × 최종 렌더 높이 2종(1080·1920)을 훑은 실측 상한이 **양방향 2.1px**이다
 *   (테스트가 같은 스윕으로 잠근다). 그래서 이 값은 "픽셀 정확한 하단"이 아니라 **2px 안에서 붙는
 *   추정**이다 — 이 이슈가 없앤 오탐은 1em(88px) 규모였으므로 그 여유로 충분하다.
 *   같은 이유로 테두리 최소 두께(`borderMinPx`)도 em 판에는 못 싣는다 — fs 가 그 하한에 걸릴 만큼
 *   작을 때만(plain 기준 fs<24) 최대 0.75px 덜 잡는다.
 *
 * @param lineCount 실제로 그리는 줄 수(= `text.split("\n").filter(Boolean).slice(0,2).length`).
 * @param hasBadge  뱃지(sub)를 실제로 그리는가.
 * @param skin      그 아이템의 **실효 스킨**(`resolveInfographicSkin(item)`). 없으면 글로우·테두리 0.
 */
export function infographicTitleBandMetricsEm(
  lineCount: number,
  hasBadge: boolean,
  skin?: InfographicBandSkinInput,
): {
  /** 몸통(글자가 앉는 판) 높이 */ heightEm: number;
  /** 실제 번짐 반경(0 이면 글로우 없음) */ glowBlurEm: number;
  /** 테두리가 몸통 밖으로 나가는 길이(= 선 두께의 절반) */ borderOutsetEm: number;
  /** 중심 y → **보이는 하단**까지의 거리 */ bottomOffsetEm: number;
} {
  const layout = INFOGRAPHIC_LAYOUT.titleBand;
  const heightEm = titleBandHeight(1, titleBandLineCount(lineCount), hasBadge, layout.badgeFontEm);
  const glowBlurEm = bandSkinAmount(skin?.glow) * layout.glowBlurEm;
  const borderOutsetEm = bandSkinAmount(skin?.border) * layout.borderWidthEm / 2;
  return {
    heightEm,
    glowBlurEm,
    borderOutsetEm,
    bottomOffsetEm: heightEm / 2 + Math.max(glowBlurEm, borderOutsetEm),
  };
}

/**
 * 💥 **하단 버스트 배너 기하 SSOT**(#1885) — 캔버스·React·배치판 경고가 모두 읽는다.
 * 반환 단위는 아이템 글자 높이(fs)의 배수(em)다.
 * ⚠️ **1 ULP 고지** — 종전 렌더러는 `fs * skin.glow * 1.6`(좌결합)이었고 지금은 `fs * (glow × 1.6)` 이라
 *   결합 순서가 바뀐다. 실측 스윕(fs 1~4000)에서 약 1/3 이 마지막 비트 하나만큼 다르다(예: gold fs=148 →
 *   71.04 vs 71.03999999999999). 번짐 반경이므로 눈에 보이는 차이는 없지만, React 쪽은 이 값이
 *   `drop-shadow` **CSS 문자열**로 나가므로 DOM 스냅샷이 비트 동일하지는 않다 — 알고 받아들인 차이다.
 *
 * @param lineCount 실제로 그리는 줄 수(= `[text, ...(sub ? [sub] : [])].length` · 2 이상이면 2줄 몸통).
 * @param glow      그 아이템의 **실효 스킨** glow(`resolveInfographicSkin(item).glow`). 0 이면 번짐 없음.
 *
 * 🕳 **두 인자는 `undefined` 도 받는다 — 타입이 아니라 아래 본문이 그 계약의 주인이다.** 판정이
 *   `Number.isFinite`(강제 변환 없는 전역 술어)인 이유가 바로 그것이다: NaN 뿐 아니라 저장분·에이전트
 *   JSON 처럼 타입 밖에서 들어오는 값도 여기서 **보수적인 쪽**으로 떨어져야 한다. 시그니처가 `number`
 *   만 말하면 그 방어가 타입 위에서는 거짓이 되고, 호출부가 "여기는 못 온다"고 오해한다.
 *   ⚠️ 넓히기는 **타입에만** 있다 — 런타임 분기는 종전과 값·순서까지 동일하다(아래 주석 참조).
 * 반환 단위는 전부 em(= 그 아이템의 글자 높이 `fs` 배수). 정규화 좌표계에서는 `sizePct` 를 곱하면 된다.
 *   ⚠️ 이 등가는 **최종 렌더 해상도**(9:16 1080×1920 · 16:9 1920×1080)에서만 정확하다 —
 *     `fs = infographicFontPx(sizePct, H)` 의 10px 하한이 H≥1000 에서는 슬라이더 하한 0.01 에도 안 걸리기
 *     때문이다. 배치판 **프리뷰**는 보드 실픽셀(흔히 356~640)로 그려서 아주 작은 크기에서는 하한에 걸려
 *     화면상 배너가 이 추정보다 커 보일 수 있다. 경고가 재는 대상은 최종 렌더이므로 그 괴리는 의도다.
 *
 * ⚖️ EF 추정기(`infographic-director-normalize.ts`)는 "그림자 blur 는 반투명이라 제외"를 규약으로 쓰는데
 *   여기는 반대로 glow 를 하단에 **더한다**. 갈리는 이유: 저쪽 그림자는 검은 반투명 드롭섀도라 자막
 *   가독성에 영향이 없고, 배너 glow 는 **accent 색으로 밝게 번지는 연출**이라 그 위에 자막이 오면 실제로
 *   읽기 어려워진다. (기능 충돌은 없다 — `bannerBurst` 는 EF 의 디렉터 8종 밖이라 저 함수가 안 만진다.)
 */
export function infographicBannerBurstMetrics(lineCount: number | undefined, glow: number | undefined): {
  /** 톱니를 뺀 몸통(글자가 앉는 판)의 높이 */ bodyHeightEm: number;
  /** 위/아래 톱니 한쪽 길이 */ toothEm: number;
  /** 톱니까지 포함한 전체 높이 */ totalHeightEm: number;
  /** 실제 번짐 반경 */ glowBlurEm: number;
  /** 중심 y → 보이는 하단까지의 거리 */ bottomOffsetEm: number;
} {
  const layout = INFOGRAPHIC_LAYOUT.bannerBurst;
  // ★비정상 입력은 **보수적인 쪽**으로 떨어뜨린다★ — 이 값이 경고 판정에 쓰이므로 NaN 이 새면
  //   `NaN > safeTop` 이 false 라 경고가 조용히 꺼진다(fail-open). 유한하지 않으면 큰 몸통을 쓴다.
  // ★아래 `typeof … === "number" ? … : NaN` 은 **런타임 무변화**다★ — `Number.isFinite` 는 강제 변환을
  //   하지 않아 숫자가 아닌 값에는 언제나 false 다. 즉 비-number 를 NaN 으로 접어 넣어도 판정 결과가
  //   같고(둘 다 "유한하지 않음" 갈래), 숫자는 그대로 통과한다. 넓힌 시그니처를 strictNullChecks
  //   컴파일 단위에서도 캐스트 없이 성립시키기 위한 형태일 뿐, 분기·값·순서는 종전과 동일하다.
  const lines = typeof lineCount === "number" ? lineCount : Number.NaN;
  const bodyHeightEm = !Number.isFinite(lines) || lines > 1
    ? layout.twoLineHeightEm
    : layout.oneLineHeightEm;
  const toothEm = layout.toothEm;
  // 음수 glow 는 스킨 레지스트리에 없지만, 들어와도 번짐을 "음수"로 만들어 하단을 위로 당기지 않는다.
  const glowEm = typeof glow === "number" ? glow : Number.NaN;
  const glowBlurEm = (Number.isFinite(glowEm) ? Math.max(0, glowEm) : 0) * layout.glowBlurEm;
  return {
    bodyHeightEm,
    toothEm,
    totalHeightEm: bodyHeightEm + toothEm * 2,
    glowBlurEm,
    bottomOffsetEm: bodyHeightEm / 2 + toothEm + glowBlurEm,
  };
}

// ─── retired #1818 derived-banner machinery (#2024 W1/W2) ────────────────────
// Consumer-time y derivation — caption contexts, clamp plans, the seed-default
// helper, and the effective-y resolver — was fully retired. Every consumer moved
// off it and the style seed no longer emits bannerBurst overlays, so the stored y
// is the single source of truth. No compatibility shim survives: a repo-wide grep
// confirmed zero remaining callers (only this tombstone and the reopen guard in
// tests/unit/issue-1818-gen-banner-caption.test.tsx, which forbids the retired
// symbols — including the bare seed helper — from returning to this file).

/**
 * 📐 **과밀 방지 상한**(Codex 1R P2-7) — AI 디렉터(infographic-director)가 프롬프트로만 지키던 밀도
 *   규약을 수동 추가 경로와 **같은 값**으로 공유한다. 값이 갈라지면 "AI 는 2개까지인데 손으로는 무한"이
 *   되어 한 컷이 오버레이로 도배된다.
 *   ★per-cut 만 강제하고 작업물 전체 총량은 강제하지 않는다★ — 전체 8은 AI **1회 생성 배치**의 규약이라
 *     유저의 누적 편집에 그대로 씌우면 "더는 못 얹는" 막다른 길이 된다(헌법 위반).
 */
export const INFOGRAPHIC_MAX_PER_CUT = 4;

/** 자동 배치가 같은 자리로 판정하는 근접 거리(정규화) — 9궁 프리셋 간격(≥0.2)보다 훨씬 작다. */
const INFOGRAPHIC_OVERLAP_EPS = { x: 0.06, y: 0.05 };

export interface InfographicAddRejection { reason: "full" | "overlap"; message: string }

/**
 * ➕ **아이템 추가 거절 사유**(공용 검증 — 수동 추가·AI 배치 공용 재료). ★null = 추가해도 된다★.
 *   ① 컷당 상한 초과 거절 ② 같은 자리에 이미 아이템이 있으면 거절(겹쳐 쌓이면 아래 것이 안 보이는데,
 *   화면상 "추가는 됐다"고 보여서 유저가 원인을 못 찾는다). 둘 다 **거절 + 안내**까지가 계약이다.
 *   ★풀와이드(bannerBurst)는 x 를 안 쓰므로 y 만 본다.
 */
// 겹침 판정은 저장 좌표(y ?? 0)를 그대로 본다. #1818 의 소비 시점 파생이 은퇴한 뒤로
// "실효 y" 와 저장 y 가 언제나 같아, 종전의 주입형 `InfographicEffectiveY` 해석기 seam 은
// 생산자 0 이 되어 함께 걷었다(#2024 W1/W2 · 되살리려면 그 파생 결론부터 뒤집어야 한다).
const storedY = (it: Pick<InfographicItem, "y">): number => it.y ?? 0;

export function infographicAddRejection(
  existing: InfographicItem[] | undefined | null,
  item: InfographicItem,
): InfographicAddRejection | null {
  const cur = (existing ?? []).filter(Boolean);
  if (cur.length >= INFOGRAPHIC_MAX_PER_CUT) {
    return { reason: "full", message: `한 컷에는 인포그래픽을 최대 ${INFOGRAPHIC_MAX_PER_CUT}개까지 얹을 수 있어요 — 기존 것을 지우고 다시 시도해 주세요.` };
  }
  const fullWide = (k: InfographicKind) => k === "bannerBurst";
  const itemY = storedY(item);
  const hit = cur.some((o) => {
    if (Math.abs(storedY(o) - itemY) >= INFOGRAPHIC_OVERLAP_EPS.y) return false;
    if (fullWide(o.kind) || fullWide(item.kind)) return true; // 화면 전폭이라 x 가 달라도 겹친다
    return Math.abs((o.x ?? 0) - item.x) < INFOGRAPHIC_OVERLAP_EPS.x;
  });
  if (hit) return { reason: "overlap", message: "그 자리엔 이미 인포그래픽이 있어요 — 위치를 바꾸거나 기존 것을 지워 주세요." };
  return null;
}

/**
 * 📋 **배치 추가 계획**(Codex 2R — AI 배치 경로 우회 봉합). 여러 아이템을 한 번에 얹는 경로(AI 디렉터·
 *   자동 치료)는 아이템마다 `infographicAddRejection` 을 **직전 수락분까지 반영한 현재 배열** 기준으로
 *   다시 물어야 한다. 원본 배열로만 검사하면 "한 번에 5개"가 상한 4를 그대로 넘어가고, 같은 배치 안에서
 *   서로 겹치는 두 개도 통과한다.
 *   ★거절은 배치 전체를 버리지 않고 **그 아이템만** 걸러낸다★ — AI 1회 배치 규약(부분 성공)을 지킨다.
 *   반환 `next` = 기존 + 수락분(그대로 저장하면 된다). 기존 배열은 손대지 않는다(정규화·필터 없음).
 */
export function planInfographicAdds(
  existing: InfographicItem[] | undefined | null,
  adds: readonly InfographicItem[],
): { accepted: InfographicItem[]; next: InfographicItem[]; rejected: InfographicAddRejection[] } {
  const next = [...(existing ?? [])];
  const accepted: InfographicItem[] = [];
  const rejected: InfographicAddRejection[] = [];
  for (const item of adds) {
    const r = infographicAddRejection(next, item);
    if (r) { rejected.push(r); continue; }
    next.push(item);
    accepted.push(item);
  }
  return { accepted, next, rejected };
}

/**
 * 🗣 **제외 사유 요약 문구** — 자동 배치는 조용히 버리면 "AI 가 5개 얹었다는데 3개뿐"이 된다.
 *   거절이 없으면 빈 문자열(호출부가 그대로 이어붙일 수 있게).
 */
export function infographicRejectionSummary(rejected: readonly InfographicAddRejection[]): string {
  if (!rejected.length) return "";
  const full = rejected.filter((r) => r.reason === "full").length;
  const overlap = rejected.filter((r) => r.reason === "overlap").length;
  const parts: string[] = [];
  if (full) parts.push(`컷당 ${INFOGRAPHIC_MAX_PER_CUT}개 상한 ${full}개`);
  if (overlap) parts.push(`자리 겹침 ${overlap}개`);
  return `${rejected.length}개는 제외했어요(${parts.join(" · ")})`;
}

/** 스킨 해석 — 미지정이면 plain, item.color 가 있으면 포인트 색만 덮어쓴다(유저 커스터마이즈 경로 보존). */
export function resolveInfographicSkin(item: Pick<InfographicItem, "skin" | "color">): InfographicSkinSpec {
  const base = INFOGRAPHIC_SKINS[item.skin ?? INFOGRAPHIC_DEFAULT_SKIN];
  return item.color ? { ...base, accent: item.color } : base;
}

/** 문자열 없는 결정론 해시 → 0..1 — 파티클 배치 전용(작은 정수 믹서). */
function spark01(n: number, seed: number): number {
  let x = (Math.imul(n, 374761393) + Math.imul(seed, 668265263)) >>> 0;
  x = (x ^ (x >>> 13)) >>> 0;
  x = Math.imul(x, 1274126177) >>> 0;
  return ((x ^ (x >>> 16)) >>> 0) / 4294967296;
}

/**
 * 🎆 glyphReveal 방사형 스파크 배치 — ★결정론★(Math.random·Date.now 금지: 렌더 코드는 같은 프레임을
 *   다시 그리면 같은 그림이어야 한다. 프레임마다 난수를 뽑으면 불티가 매 프레임 순간이동한다).
 *   양 렌더러(캔버스·React)가 이 함수 하나를 공유해 같은 불티를 그린다.
 *   반환: angle(라디안) · dist(글리프 높이 대비 최종 비행 거리 배율) · size(글리프 높이 대비 크기 배율).
 */
export function glyphSparks(count = 14, seed = 7): Array<{ angle: number; dist: number; size: number }> {
  const n = Math.max(0, Math.min(48, Math.round(count)));
  const out: Array<{ angle: number; dist: number; size: number }> = [];
  for (let i = 0; i < n; i++) {
    const a = spark01(i * 2 + 1, seed);
    const b = spark01(i * 2 + 2, seed);
    out.push({
      angle: (i / n) * Math.PI * 2 + (a - 0.5) * 0.6, // 고르게 퍼지되 기계적 등간격은 피한다
      dist: 0.62 + a * 0.7,
      size: 0.035 + b * 0.055,
    });
  }
  return out;
}

/**
 * 🧹 **은퇴 마커 소거**(적대 R1 P2 — 재시드하지 않는 기존 저장 작업물) — source:"style" 아이템을
 *   걸러낸다. 🪦 파생 축 철거(2026-08-08)로 슬롯 문안을 채우는 주체가 **애초에 배선된 적 없어**,
 *   저장분에 남은 마커 아이템은 전부 미충전 플레이스홀더(〔시리즈명〕·〔문항〕·"?" 글리프)다 —
 *   재시드는 carryOverUserInfographics 가 청소하지만, 재시드하지 않는 작업물에는 그대로 렌더된다.
 *   ★모든 읽기·투영·렌더 경계가 이 함수(또는 노드 판 withoutRetiredStyleInfographics)를 쓴다★:
 *   ① 캔버스 하이드레이트(toRFNode — 다음 저장 때 자연 영속) ② 노드 투영(gen 미리보기 코드·노드
 *   인계) ③ 저장 경계(producedCuts 쓰기 liveInfographicsOf · 읽기 restoreProducedCuts) ④ 캔버스→
 *   에이전트 code 승계(cinematicToStory) ⑤ 에이전트 편집 패널 읽기(readStorySceneInfographics)
 *   ⑥ 양 렌더러(drawInfographics·SceneInfographics — 어느 관문도 안 지난 잔존분의 최종 방벽).
 *   ★예외는 타임라인 칩(deriveStoryTimeline) 하나★ — 칩 인덱스가 편집 patch 의 배열 인덱스와
 *   결합돼 있어 표시만 걸러내면 유저가 엉뚱한 아이템을 고친다(상류에서 걸러 자연히 깨끗해진다).
 *   유저가 손댄 아이템은 마커가 이미 떨어져 있으므로(promote) 여기서 절대 걸러지지 않는다(막다른 길
 *   금지). 타임라인에 남은 마커 아이템도 편집하는 순간 승격되어 다시 렌더된다 — 소거는 비파괴다.
 *   반환: 걸러낼 게 없으면 **입력 그대로**(사본 0), 전부 걸러지면 undefined(엔벨로프 직렬화 때 키 탈락).
 */
export function dropRetiredStyleInfographics(
  items: InfographicItem[] | undefined | null,
): InfographicItem[] | undefined {
  if (!items?.length) return items ?? undefined;
  const kept = items.filter((it) => it && it.source !== "style");
  if (kept.length === items.length) return items;
  return kept.length ? kept : undefined;
}

/**
 * 🧹 노드/컷 단위 소거(위 함수의 객체 판) — 저장 노드를 **다른 표면으로 투영**하는 경계가 전부 이걸
 *   쓴다(캔버스 하이드레이트 toRFNode · gen 미리보기 코드 · 노드 인계 투영). 투영 경계마다 손으로
 *   `{...n, infographics: filter(...)}` 를 적으면 한 곳만 빠져도 그 표면에서 플레이스홀더가 되살아난다.
 *   ★걸러낼 게 없으면 **같은 객체 참조**를 돌려준다★ — React 메모·얕은 비교 기반 저장 디바운스가
 *   "안 바뀐 노드"를 바뀐 것으로 오인하지 않게(불필요한 재렌더·재저장 0).
 */
export function withoutRetiredStyleInfographics<T extends { infographics?: InfographicItem[] }>(node: T): T {
  if (!node || typeof node !== "object") return node;
  const kept = dropRetiredStyleInfographics(node.infographics);
  return kept === node.infographics ? node : { ...node, infographics: kept };
}

/**
 * 🔁 재시드·스타일 교체 병합 — **source:"style" 아이템만** 새 파생분으로 갈아끼우고 유저 아이템은
 *   순서대로 보존한다. 🪦 시드 파생 철거(2026-08-08) 후 유일 호출부 = canvas-seed-policy 의
 *   carryOverUserInfographics(derived 는 이제 항상 빈 배열 — 저장된 style 플레이스홀더는 재시드 때
 *   대체 없이 청소된다).
 *   ★유저가 손댄 아이템은 마커가 떨어져 있으므로 여기서 절대 지워지지 않는다(막다른 길 금지).
 */
export function replaceStyleInfographics(
  existing: InfographicItem[] | undefined | null,
  derived: InfographicItem[],
): InfographicItem[] {
  const kept = (existing ?? []).filter((it) => it && it.source !== "style");
  return [...kept, ...derived];
}

/**
 * 🏷→👤 **유저 승격**(제자리 변형) — 스타일 파생 아이템을 유저가 **편집하는 순간** source 마커를 뗀다.
 *   ★없으면 어떤 일이 벌어지나(Codex 1R P1-2 실측)★: 타임라인에서 파생 문구를 고쳐도 마커가 남아 있어,
 *     다음 재시드·스타일 교체가 그 아이템을 "스타일 것"으로 보고 통째로 갈아끼운다 = **유저가 쓴 문구가
 *     소리 없이 삭제된다**. 편집 UI 가 폼 값으로 새 아이템을 만드는 경로(정교 조정 패널)는 애초에 마커가
 *     안 붙지만, **제자리 수정** 경로(타임라인 문구·표시창)는 이 함수를 반드시 통과해야 한다.
 *   반환 = 실제로 마커를 뗐는가(호출부 로깅·테스트용).
 */
export function promoteInfographicToUser(
  item: { source?: unknown } | null | undefined,
): boolean {
  if (!item || typeof item !== "object" || (item as InfographicItem).source !== "style") return false;
  delete (item as { source?: unknown }).source;
  return true;
}

/** UI 카탈로그(바로제작 패널·이후 에이전트 UI 공용) — 라벨·입력 힌트. */
export const INFOGRAPHIC_PRESETS: Array<{ kind: InfographicKind; label: string; hint: string }> = [
  { kind: "badge", label: "강조 배지", hint: "문구 — 예: 오늘만 특가" },
  { kind: "price", label: "가격 카드", hint: "가격 — 예: 19,900원 (원가는 보조란)" },
  { kind: "stat", label: "숫자 카운트업", hint: "목표 숫자 — 예: 87 (+접미사 %)" },
  { kind: "arrow", label: "화살표 라벨", hint: "문구 — 예: 이 부분 주목" },
  { kind: "list", label: "체크 리스트", hint: "제목(문구) + 줄들(쉼표 구분) — 예: 포인트, 3가지" },
  { kind: "compare", label: "비교(VS)", hint: "왼쪽=문구·오른쪽=보조 — 예: 일반 vs 이 제품" },
  { kind: "ring", label: "강조 링", hint: "라벨(옵션) — 주목 지점에 원형 하이라이트" },
  { kind: "bar", label: "게이지 바", hint: "라벨 + 숫자(0~100) — 예: 만족도 92" },
  // 📽 P3 4종 — 유저가 직접 얹는 장치(🪦 스타일 자동 파생은 2026-08-08 철거 — 이 카탈로그 경로만 남았다).
  { kind: "panelCard", label: "문제판 패널", hint: "제목(문구) + 본문(보조란·여러 줄은 쉼표)" },
  { kind: "glyphReveal", label: "대형 글리프 리빌", hint: "글자 1~2자 — 예: O · X · ! · ?" },
  { kind: "bannerBurst", label: "하단 버스트 배너", hint: "굵은 한 줄(둘째 줄은 보조란)" },
  { kind: "titleBand", label: "상단 타이틀 밴드", hint: "카피(문구) + 뱃지(보조란)" },
];

/**
 * 📐 **배너·타이틀밴드 폭 비율**(프레임 폭 대비) — 규격값(`bannerBurst.widthPct` 98% · `titleBand.maxWidthPct` 94%)을
 * **가운데 정렬 ui 안전 폭**으로 상한한다(세이프존 3-b · 회장님 09-26 승인). 세로판은 오른쪽 버튼 열(14.75%) 때문에 70.5%,
 * 가로판은 91.7%. 배너·밴드는 제목류라 빗금 구역까지 쓰고 ui 층만 피한다(3-a 로고·제목띠와 같은 층 규칙).
 * ★캔버스(`cinematic-infographic-draw`)와 Remotion(`SceneInfographics`)이 같은 함수를 읽는다★ — 한쪽만 좁히면 미리보기와 결과가 갈린다.
 */
export function infographicWideWidthFrac(kind: "bannerBurst" | "titleBand", frameW: number, frameH: number): number {
  const spec = kind === "bannerBurst" ? INFOGRAPHIC_LAYOUT.bannerBurst.widthPct : INFOGRAPHIC_LAYOUT.titleBand.maxWidthPct;
  if (!(frameW > 0 && frameH > 0)) return spec;
  return Math.min(spec, centeredSafeWidth(nearestSafeAspect(frameW, frameH), "ui"));
}

/** 글자 줄 폭 밖에 남기는 몫(em · 글자 크기 배수) — 배너 = 안쪽 판 들여쓰기 0.35×2 + 숨 0.3×2 · 밴드 = 좌우 패딩 1.2×2. */
const WIDE_TEXT_PAD_EM = { bannerBurst: 1.3, titleBand: 2.4 } as const;
/** 줄여도 이 비율 밑으로는 안 줄인다 — 그보다 긴 카피는 넘치는 쪽이 «안 읽히는 글자»보다 낫다. */
const WIDE_TEXT_MIN_RATIO = 0.5;

/**
 * 📐 **배너·타이틀밴드 글자 크기 맞춤**(세이프존 3-b) — 폭을 가운데 정렬 안전 폭으로 좁히자(98% → 세로 70.5%) 종전 폭에
 * 들어가던 카피가 배너 밖으로 넘쳤다(실렌더 «오늘만 특가 한정 수량»). 가장 긴 줄의 글자 폭 근사(`visualEmWidth`)로
 * 안쪽 폭에 들게 **줄이기만** 한다(키우지 않는다 · 하한 50%). 배너 모양(높이·톱니)은 원래 크기 그대로다.
 * ★캔버스와 Remotion 이 같은 수를 쓴다★ — 한쪽이 실측(measureText)으로 맞추면 두 렌더러의 글자 px 가 갈린다.
 */
export function infographicWideTextPx(kind: "bannerBurst" | "titleBand", fontPx: number, lines: readonly string[], outerWidthPx: number): number {
  const maxEm = lines.reduce((m, li) => Math.max(m, visualEmWidth(li)), 0);
  if (!(fontPx > 0) || !(maxEm > 0) || !(outerWidthPx > 0)) return fontPx;
  const fit = (outerWidthPx - fontPx * WIDE_TEXT_PAD_EM[kind]) / maxEm;
  return Math.min(fontPx, Math.max(fontPx * WIDE_TEXT_MIN_RATIO, fit));
}

/**
 * 📐 위치 프리셋 격자 — **화면비별 세이프존 core 안**(회장님 2026-09-23 전역 적용 3단계).
 * 종전 격자(윗줄 14~16% · 오른쪽 76~78% · 아랫줄 62%) 한 벌은 세로 쇼츠의 위쪽 아이콘·제목 자리, 오른쪽 버튼 열,
 * 세이프존으로 올라온 자막(62.9% 부터)에 걸렸다. 같은 식을 **그 화면비의 core·자막선**에 건다:
 *   · 윗줄 = core 상단 + 7%   · 가운데 줄 = 42%(종전 그대로)   · 아랫줄 = 그 화면비 자막선 − 9%(아이템 반높이 여유)
 *   · 왼쪽 열 = core 왼쪽 + 20%   · 가운데 = 50%   · 오른쪽 열 = core 오른쪽 − 19%
 * ★한 벌로 두지 않는 이유★: 세로 core 는 오른쪽 버튼 열(14.75%) 때문에 비대칭이라, 그 격자를 가로에 쓰면
 *   «우중»이 0.66 으로 가운데 쪽에 쏠린다(가로는 좌우 띠가 없어 대칭 0.24/0.77 이 맞다).
 * 가로 외 화면비(4:5·1:1 등)는 세로 격자 — 9:16 core 가 가장 좁아 그 안쪽이면 다른 세로형 core 에서도 안쪽이다.
 * 저장된 좌표는 옮기지 않는다(회장님 09-23 ③) — 이 표는 새로 놓을 때의 자리만 정한다.
 */
export interface InfographicPositionPreset { id: string; label: string; x: number; y: number }

const PRESET_CELLS: ReadonlyArray<readonly [id: string, label: string, col: "l" | "c" | "r", row: "t" | "m" | "b"]> = [
  ["tl", "좌상", "l", "t"], ["tc", "중상", "c", "t"], ["tr", "우상", "r", "t"],
  ["ml", "좌중", "l", "m"], ["mc", "중앙", "c", "m"], ["mr", "우중", "r", "m"],
  ["bl", "좌하", "l", "b"], ["bc", "중하", "c", "b"], ["br", "우하", "r", "b"],
];

function presetGrid(aspect: InfographicAspect): InfographicPositionPreset[] {
  const core = SAFE_ZONE[aspect].core;
  const col = { l: core.left + 0.2, c: 0.5, r: 1 - core.right - 0.19 };
  const row = { t: core.top + 0.07, m: 0.42, b: captionSafeTopOf(aspect) - 0.09 };
  return PRESET_CELLS.map(([id, label, c, r]) => ({ id, label, x: col[c], y: row[r] }));
}

const PRESET_GRID: Readonly<Record<InfographicAspect, InfographicPositionPreset[]>> = {
  "9:16": presetGrid("9:16"),
  "16:9": presetGrid("16:9"),
};

/** 위치 프리셋(9궁 — UI 셀렉트용) · 세로 격자. 화면비를 아는 곳은 `infographicPositionsFor` 를 쓴다. */
export const INFOGRAPHIC_POSITIONS: InfographicPositionPreset[] = PRESET_GRID["9:16"];

/** 🧭 화면비(`"16:9"` 류 문자열)에 맞는 9궁 — 가로(폭>높이)는 가로 격자, 그 밖은 세로 격자. id·label 은 두 벌이 같다. */
export function infographicPositionsFor(aspect: string | null | undefined): InfographicPositionPreset[] {
  const [w, h] = String(aspect ?? "").split(":").map(Number);
  return w > h ? PRESET_GRID["16:9"] : PRESET_GRID["9:16"];
}

/** 입력 정규화 — UI/승계 경로 공용(좌표 클램프·기본값 채움·빈 텍스트 거부). null=버릴 아이템. */
export function normalizeInfographic(raw: Partial<InfographicItem> | null | undefined): InfographicItem | null {
  if (!raw || typeof raw !== "object") return null;
  const kind = raw.kind;
  if (!kind || !(kind in INFOGRAPHIC_DEFAULT_SIZE)) return null;
  const text = String(raw.text ?? "").trim();
  if (!text && kind !== "ring") return null; // ring 은 라벨 없이 링만도 유효
  const clamp = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : d);
  const item: InfographicItem = {
    kind,
    text: text.slice(0, 40),
    x: clamp(raw.x, 0.5),
    y: clamp(raw.y, 0.4),
  };
  if (raw.value != null && String(raw.value).trim() !== "") item.value = typeof raw.value === "number" ? raw.value : String(raw.value).slice(0, 24);
  if (raw.sub != null && String(raw.sub).trim() !== "") item.sub = String(raw.sub).slice(0, 24);
  if (typeof raw.sizePct === "number" && raw.sizePct >= 0.01 && raw.sizePct <= 0.2) item.sizePct = raw.sizePct;
  if (typeof raw.color === "string" && /^#[0-9a-fA-F]{3,8}$/.test(raw.color)) item.color = raw.color;
  if (typeof raw.start === "number" && raw.start >= 0) item.start = raw.start;
  if (typeof raw.end === "number" && raw.end > (item.start ?? 0)) item.end = raw.end;
  if (raw.tip && typeof raw.tip === "object") item.tip = { x: clamp(raw.tip.x, item.x), y: clamp(raw.tip.y, Math.min(1, item.y + 0.15)) };
  // 🎨 스킨 — 레지스트리에 없는 값은 조용히 버린다(기본 plain 으로 렌더). 오염 JSON 이 룩을 못 깬다.
  if (typeof raw.skin === "string" && raw.skin in INFOGRAPHIC_SKINS) item.skin = raw.skin as InfographicSkin;
  // 🏷 출처 마커 — 받는 값은 "style" 하나뿐. 그래서 편집 UI 가 폼 값으로 만든 아이템에는 절대 안 붙고,
  //   영속 왕복(cinematicToStory 재통과)에서는 살아남는다 = 재시드가 스타일 파생분만 정확히 지운다
  //   (🪦 파생 0 이후 갈아끼울 새 분량은 없다 — 남은 style 마커는 청소 대상 표식).
  if (raw.source === "style") item.source = "style";
  if (Array.isArray(raw.items)) {
    const items = raw.items.map((v) => String(v ?? "").trim()).filter(Boolean).map((v) => v.slice(0, 24)).slice(0, 6);
    if (items.length) item.items = items;
  }
  // list 는 항목이 실체 — 항목 0개면 버린다(빈 카드 방지). bar 는 value 숫자(0~100 클램프) 필수.
  if (item.kind === "list" && !item.items?.length) return null;
  // glyphReveal 은 화면을 가득 채우는 **단일 글리프**다 — 문장이 들어오면 레이아웃이 무너지므로 2자로 자른다
  //   (버리지 않는다: 자르면 O·X·!·? 의도는 살고, 버리면 리빌 컷이 통째로 빈다).
  if (item.kind === "glyphReveal") {
    item.text = Array.from(item.text).slice(0, 2).join("");
    if (!item.text) return null;
  }
  if (item.kind === "bar") {
    // value 부재/빈 문자열은 게이지 대상이 없음 — null(★String(undefined??"")→Number("")=0 오통과 방지).
    const src = item.value;
    const n = typeof src === "number" ? src : src != null && String(src).trim() !== "" ? Number(String(src).replace(/[^\d.-]/g, "")) : NaN;
    if (!Number.isFinite(n)) return null;
    item.value = Math.max(0, Math.min(100, Math.round(n)));
  }
  return item;
}
